import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth-context'

export function OnboardingCompletePage() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()

  useEffect(() => {
    if (loading) return

    if (!user) {
      navigate('/login', { replace: true })
      return
    }

    if (user.onboardingCompleted) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, loading, navigate])

  if (loading || !user || user.onboardingCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const handleFinish = () => {
    navigate('/dashboard')
  }

  const handleBack = () => {
    navigate('/onboarding/team')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center p-4 lg:p-8">
      <div className="bg-card border border-border shadow-clean rounded-3xl shadow-clean overflow-hidden w-full max-w-7xl p-8 lg:p-16">
        <div className="text-center mb-16">
          <div className="inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-accent to-primary text-primary-foreground font-bold text-3xl mb-8 shadow-clean">
            ✓
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-brand mb-6 tracking-tight">You&apos;re all set!</h2>
          <p className="text-xl lg:text-2xl text-brand-secondary leading-relaxed max-w-4xl mx-auto">
            Welcome to FeedbackPro. Here&apos;s what you can do next:
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-16">
          <div className="bg-background border border-border rounded-3xl p-10 lg:p-12 text-center hover:shadow-clean transition-smooth group hover:border-indigo-200">
            <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-muted to-accent text-primary group-hover:from-muted group-hover:to-accent/80 transition-smooth shadow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-users"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-brand mb-6">Manage Your Team</h3>
            <p className="text-brand-secondary leading-relaxed text-lg">
              Add team members, assign roles, and organize your teams for optimal collaboration
            </p>
          </div>

          <div className="bg-background border border-border rounded-3xl p-10 lg:p-12 text-center hover:shadow-clean transition-smooth group hover:border-border">
            <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-3xl gradient-card text-primary group-hover:from-accent/20 group-hover:to-primary/10 transition-smooth shadow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-calendar"
              >
                <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                <line x1="16" x2="16" y1="2" y2="6" />
                <line x1="8" x2="8" y1="2" y2="6" />
                <line x1="3" x2="21" y1="10" y2="10" />
              </svg>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-brand mb-6">Schedule Feedback</h3>
            <p className="text-brand-secondary leading-relaxed text-lg">
              Create feedback cycles and set up regular 1-on-1 sessions for continuous growth
            </p>
          </div>

          <div className="bg-background border border-border rounded-3xl p-10 lg:p-12 text-center hover:shadow-clean transition-smooth group hover:border-green-200">
            <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-muted to-accent text-primary group-hover:from-muted group-hover:to-accent/80 transition-smooth shadow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-bar-chart-2"
              >
                <line x1="18" x2="18" y1="20" y2="10" />
                <line x1="12" x2="12" y1="20" y2="4" />
                <line x1="6" x2="6" y1="20" y2="14" />
              </svg>
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-brand mb-6">Track Progress</h3>
            <p className="text-brand-secondary leading-relaxed text-lg">
              Monitor team performance and individual development with powerful analytics
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 pt-12 border-t border-border">
          <Button
            variant="outline"
            onClick={handleBack}
            className="h-16 px-12 rounded-xl text-lg font-semibold hover:bg-muted/50 transition-smooth"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-3"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Back
          </Button>
          <Button onClick={handleFinish} className="h-16 px-12 font-bold text-xl rounded-xl">
            Go to Dashboard
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="ml-3"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default OnboardingCompletePage
